import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// dev/app-skeleton/src/pages/Settings.tsx
// 设置详情：
//   1) 账户：立即锁定
//      D15 Task 6：原"修改主密码（D14 接入真流程）"按钮整体隐藏 ——
//      E2EE 改密涉及旧 KEK 解 root_key → 新 KEK 重封 wrapped_key 上传，
//      留半成品风险大；等专门一轮再开。忘了密码请走 /forgot-password。
//   2) 备份：导出 L3 (.diary.enc) + 导入 / 替换全部（带 24h 撤销）
//   3) 撤销快照：列出未过期 snapshot + 倒计时 + 一键撤销
// 全部按钮 ≥ 44×44；data-testid 留给 QA TC-022/TC-083.C 等用例。
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { lock, isUnlocked } from "../lib/session-key";
import { exportBackup, suggestBackupFilename, parseBackupFile, importBackupReplaceAll, listRevertSnapshots, revertReplaceAll, } from "../lib/backup";
import { useCurrentUserId } from "../lib/current-user";
function fmtRemaining(ms) {
    if (ms <= 0)
        return "已过期";
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    return `${h}h ${m}m`;
}
export function SettingsPage() {
    const nav = useNavigate();
    const userId = useCurrentUserId();
    const [snaps, setSnaps] = useState([]);
    const [busy, setBusy] = useState("");
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");
    const [tick, setTick] = useState(0);
    // 1 min tick 用来刷新倒计时
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 60_000);
        return () => clearInterval(id);
    }, []);
    useEffect(() => {
        if (!userId)
            return;
        let alive = true;
        listRevertSnapshots(userId).then((rows) => { if (alive)
            setSnaps(rows); });
        return () => { alive = false; };
    }, [userId, tick, busy]);
    async function onExport() {
        if (!userId)
            return;
        setBusy("export");
        setErr("");
        setMsg("");
        try {
            const blob = await exportBackup(userId, { includeDrafts: false });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = suggestBackupFilename();
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            setMsg("已下载加密备份 .diary.enc · 妥善保管。");
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : "导出失败");
        }
        finally {
            setBusy("");
        }
    }
    async function onImport(file) {
        if (!userId)
            return;
        if (!confirm(`将用 "${file.name}" 替换本地全部数据。\n替换前会自动加密快照当前库到本机，24h 内可撤销。继续？`))
            return;
        setBusy("import");
        setErr("");
        setMsg("");
        try {
            const parsed = await parseBackupFile(file);
            const res = await importBackupReplaceAll(userId, parsed);
            setMsg(`替换完成 · 写入 ${res.imported_entries} 条 entries + ${res.imported_drafts} 条 drafts；可在下方列表 24h 内撤销 (snapshot ${res.revert_snapshot.snapshot_id.slice(0, 8)})。`);
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : "导入失败");
        }
        finally {
            setBusy("");
        }
    }
    async function onRevert(snapId) {
        if (!userId)
            return;
        if (!confirm("用该快照覆盖本地当前数据？此操作不可二次撤销。"))
            return;
        setBusy("revert");
        setErr("");
        setMsg("");
        try {
            const r = await revertReplaceAll(userId, snapId);
            setMsg(`撤销完成 · 恢复 ${r.restored_entries} 条 entries · ${r.restored_drafts} 条 drafts。`);
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : "撤销失败");
        }
        finally {
            setBusy("");
        }
    }
    const sortedSnaps = useMemo(() => [...snaps].sort((a, b) => b.created_at - a.created_at), [snaps]);
    return (_jsxs("section", { className: "px-md py-lg max-w-2xl mx-auto", "data-testid": "page-settings", children: [_jsx("h1", { className: "text-2xl font-medium mb-lg", children: "\u8BBE\u7F6E" }), msg && _jsx("div", { className: "text-success text-sm mb-sm", role: "status", "data-testid": "settings-msg", children: msg }), err && _jsx("div", { className: "text-danger text-sm mb-sm", role: "alert", "data-testid": "settings-err", children: err }), _jsxs("div", { className: "card p-md mb-md", children: [_jsx("h2", { className: "font-medium mb-sm", children: "\u8D26\u6237" }), _jsx("button", { className: "btn btn-ghost w-full justify-start", style: { minHeight: 44 }, onClick: () => { lock(); nav("/unlock", { replace: true }); }, "data-testid": "btn-lock", children: "\u7ACB\u5373\u9501\u5B9A" }), _jsx("p", { className: "text-text-muted text-xs mt-sm", children: "\u9501\u5B9A\u540E\u9700\u8981\u91CD\u65B0\u8F93\u5165\u5BC6\u7801\u624D\u80FD\u67E5\u770B\u65E5\u8BB0\u3002" })] }), _jsxs("div", { className: "card p-md mb-md", children: [_jsx("h2", { className: "font-medium mb-sm", children: "\u5907\u4EFD" }), _jsx("button", { className: "btn btn-primary w-full mb-sm", style: { minHeight: 44 }, onClick: onExport, disabled: busy === "export" || !userId, "data-testid": "btn-export", children: busy === "export" ? "导出中…" : "导出 L3 加密备份（.diary.enc）" }), _jsxs("label", { className: "btn btn-ghost w-full justify-start cursor-pointer", style: { minHeight: 44 }, "data-testid": "label-import", children: [_jsx("span", { children: busy === "import" ? "导入中…" : "导入 / 替换全部…" }), _jsx("input", { type: "file", accept: ".diary.enc,application/octet-stream", className: "hidden", disabled: busy === "import" || !userId, onChange: (e) => {
                                    const f = e.target.files?.[0];
                                    if (f)
                                        void onImport(f);
                                    e.target.value = "";
                                }, "data-testid": "input-import" })] }), _jsx("p", { className: "text-text-muted text-xs mt-sm", children: "\u5BFC\u5165\u4F1A\u7528\u5907\u4EFD\u5B8C\u5168\u66FF\u6362\u672C\u5730\u6570\u636E\uFF1B\u66FF\u6362\u524D\u81EA\u52A8\u52A0\u5BC6\u5FEB\u7167\u5F53\u524D\u5E93\u5230\u672C\u673A\uFF0C24h \u5185\u53EF\u4E0B\u65B9\u64A4\u9500\u3002" })] }), _jsxs("div", { className: "card p-md mb-md", children: [_jsx("h2", { className: "font-medium mb-sm", children: "\u64A4\u9500\u5FEB\u7167\uFF0824h \u5185\uFF09" }), !sortedSnaps.length ? (_jsx("p", { className: "text-text-muted text-sm", "data-testid": "snaps-empty", children: "\u6682\u65E0\u53EF\u64A4\u9500\u5FEB\u7167\u3002" })) : (_jsx("ul", { className: "flex flex-col gap-sm", "data-testid": "snaps-list", children: sortedSnaps.map((s) => {
                            const remaining = s.expires_at - Date.now();
                            return (_jsxs("li", { className: "border border-border rounded-md p-sm flex items-center justify-between gap-sm", children: [_jsxs("div", { className: "text-xs", children: [_jsx("div", { className: "font-mono", children: s.snapshot_id.slice(0, 8) }), _jsxs("div", { className: "text-text-muted", children: [new Date(s.created_at).toLocaleString("zh-CN"), " \u00B7 ", s.entry_count, " entries \u00B7 \u8FD8\u5269 ", fmtRemaining(remaining)] })] }), _jsx("button", { type: "button", className: "btn btn-ghost", style: { minHeight: 44 }, disabled: remaining <= 0 || busy === "revert", onClick: () => onRevert(s.snapshot_id), "data-testid": `btn-revert-${s.snapshot_id.slice(0, 8)}`, children: "\u64A4\u9500\u5230\u6B64" })] }, s.snapshot_id));
                        }) }))] }), _jsxs("p", { className: "text-text-muted text-xs mt-md", children: ["\u5F53\u524D\u72B6\u6001\uFF1A", isUnlocked() ? "已解锁" : "已锁定", " \u00B7 user_id ", _jsx("code", { className: "font-mono", children: userId?.slice(0, 8) ?? "…" })] })] }));
}
